/**
 * Dialog "Split rueckgaengig machen" (Spec 2026-10-04): zeigt die Vorschau der Rueckfuehrung einer
 * Teillieferung in ihre Ursprungsbestellung und liefert die Auswahl der zu uebernehmenden Dokumente
 * und Tasks. Aufbau und Klassen wie `TeillieferungDialog.tsx`; ohne Bestandsvorbild in `index.html`.
 * Die Vorschau holt der Dialog selbst; Bestaetigen/Ausfuehren ist Sache des Aufrufers.
 */
import { useEffect, useState, type JSX } from "react";
import { Ikone } from "../Ikone.tsx";
import { DOC_TYPES } from "../../config/documents.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import {
  rueckfuehrungsVorschauLaden,
  type RueckfuehrungsAuswahl,
  type RueckfuehrungsDokument,
  type RueckfuehrungsVorschau,
} from "../../api/splitRueckgaengig.ts";

export interface SplitRueckgaengigDialogProps {
  kennung: number;
  onAbbrechen(): void;
  onBestaetigen(auswahl: RueckfuehrungsAuswahl): void;
  laeuft: boolean;
}

/** Label des Dokumenttyps (`DOC_TYPES`), bei unbekanntem Schluessel der Schluessel selbst. */
function typLabel(docType: string): string {
  return DOC_TYPES.find((t) => t.id === docType)?.label ?? docType;
}

const GRUND_TEXT: Record<NonNullable<RueckfuehrungsDokument["grund"]>, string> = {
  schon_in_quelle: "liegt schon in der Ursprungsbestellung",
  positionsbezogen: "positionsbezogen – wird nicht übernommen",
  sendung: "Sendungsdokument – wird nicht übernommen",
  gruppe: "gehört zur Bestellgruppe – wird nicht übernommen",
};

export function SplitRueckgaengigDialog({
  kennung,
  onAbbrechen,
  onBestaetigen,
  laeuft,
}: SplitRueckgaengigDialogProps): JSX.Element {
  const [vorschau, setVorschau] = useState<RueckfuehrungsVorschau | null>(null);
  const [ladefehler, setLadefehler] = useState("");
  const [dokumentIds, setDokumentIds] = useState<ReadonlySet<string>>(new Set());
  const [taskIds, setTaskIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let aktiv = true;
    rueckfuehrungsVorschauLaden(kennung).then(
      (v) => {
        if (aktiv) setVorschau(v);
      },
      (e: unknown) => {
        // Die Zeile im Dialog gab es schon (Spec R2): neu ist der Text. Der feste Satz wird zum Kontext.
        const kontext = "Die Vorschau konnte nicht geladen werden.";
        if (aktiv) setLadefehler(fehlertext(e, kontext) ?? kontext);
      },
    );
    return () => {
      aktiv = false;
    };
  }, [kennung]);

  function umschalten(menge: ReadonlySet<string>, setze: (m: ReadonlySet<string>) => void, id: string): void {
    const neu = new Set(menge);
    if (neu.has(id)) neu.delete(id);
    else neu.add(id);
    setze(neu);
  }

  const titel = vorschau
    ? `Split rückgängig: ${vorschau.kindNummer} zurück in ${vorschau.quelleNummer}`
    : "Split rückgängig";
  const gesperrt = vorschau !== null && vorschau.sperren.length > 0;
  const fortsetzen = vorschau !== null && vorschau.offen !== null;
  // Bei einer Fortsetzung gilt die Auswahl des ersten Versuchs (das Backend nutzt sie, nicht eine neue):
  // sie erscheint als gesetzte, gesperrte Haken.
  const gespeichert = fortsetzen ? vorschau?.gespeicherteAuswahl ?? null : null;
  const dokumentGewaehlt = (id: string): boolean =>
    gespeichert ? gespeichert.dokumentIds.includes(id) : dokumentIds.has(id);
  const taskGewaehlt = (id: string): boolean => (gespeichert ? gespeichert.taskIds.includes(id) : taskIds.has(id));

  return (
    <section
      className="board-editor-dialog partial-delivery-dialog split-rueckgaengig-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="splitRueckgaengigTitel"
    >
      <div className="board-editor-head">
        <div className="board-editor-head-copy">
          <h2 id="splitRueckgaengigTitel">{titel}</h2>
        </div>
        <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
          <Ikone name="x" />
        </button>
      </div>
      <div className="board-editor-body partial-delivery-body">
        {ladefehler ? (
          <div className="new-order-error" role="alert">
            {ladefehler}
          </div>
        ) : !vorschau ? (
          <div className="board-editor-note">Vorschau wird geladen …</div>
        ) : gesperrt ? (
          <div className="new-order-error" role="alert">
            {vorschau.sperren.map((meldung) => (
              <p key={meldung}>{meldung}</p>
            ))}
          </div>
        ) : (
          <>
            {fortsetzen && (
              <div className="board-editor-note">
                Eine frühere Rückführung ist unvollständig – Bestätigen setzt sie fort.
              </div>
            )}
            <div className="partial-table-wrap">
              <table className="partial-table">
                <thead>
                  <tr>
                    <th>Artikelnummer</th>
                    <th className="num">zurück</th>
                    <th className="num">{vorschau.quelleNummer} vorher → nachher</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {vorschau.plan.map((zeile, index) => (
                    <tr key={zeile.kLieferantenBestellungPos ?? `neu-${index}`}>
                      <td className="mono">{zeile.artikelNummer ?? "—"}</td>
                      <td className="num mono">{zeile.mengeZurueck}</td>
                      <td className="num mono">
                        {zeile.mengeVorher} → {zeile.mengeNachher}
                      </td>
                      <td>
                        {zeile.art === "neu" ? <span className="split-rueckgaengig-hinweis">neue Position</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {vorschau.dokumente.length > 0 && (
              <ul className="split-rueckgaengig-liste" aria-label="Dokumente">
                {vorschau.dokumente.map((d) => {
                  const label = typLabel(d.docType);
                  const name = d.dateiname ?? label;
                  return (
                    <li key={d.linkId}>
                      {d.waehlbar ? (
                        <label>
                          <input
                            type="checkbox"
                            aria-label={`${name} übernehmen`}
                            checked={dokumentGewaehlt(d.linkId)}
                            disabled={fortsetzen}
                            onChange={() => umschalten(dokumentIds, setDokumentIds, d.linkId)}
                          />
                          <span>{label}</span>
                          {d.dateiname ? <> <span>{d.dateiname}</span></> : null}
                        </label>
                      ) : (
                        <span>
                          {label}
                          {d.dateiname ? ` ${d.dateiname}` : ""}
                        </span>
                      )}
                      {d.grund && <span className="split-rueckgaengig-hinweis">{GRUND_TEXT[d.grund]}</span>}
                      {d.waehlbar && d.alsNeueFassung && (
                        <span className="split-rueckgaengig-hinweis">wird als neue Fassung angehängt</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {vorschau.tasks.length > 0 && (
              <ul className="split-rueckgaengig-liste" aria-label="Tasks">
                {vorschau.tasks.map((t) => (
                  <li key={t.id}>
                    <label>
                      <input
                        type="checkbox"
                        aria-label={`${t.titel} übernehmen`}
                        checked={taskGewaehlt(t.id)}
                        disabled={fortsetzen}
                        onChange={() => umschalten(taskIds, setTaskIds, t.id)}
                      />
                      <span>{t.titel}</span>
                    </label>
                    {t.erledigt && <span className="split-rueckgaengig-hinweis">erledigt</span>}
                  </li>
                ))}
              </ul>
            )}
            {(vorschau.dokumente.length > 0 || vorschau.tasks.length > 0) && (
              <div className="board-editor-note">
                Nicht gewählte Dokumente und Aufgaben werden mit der Teillieferung gelöscht.
              </div>
            )}
          </>
        )}
      </div>
      <div className="board-editor-foot">
        <button className="btn-sec" type="button" onClick={onAbbrechen}>
          <Ikone name="x" />
          {gesperrt ? "Schließen" : "Abbrechen"}
        </button>
        {vorschau !== null && !gesperrt && (
          <button
            className="btn-primary"
            type="button"
            disabled={laeuft}
            onClick={() =>
              onBestaetigen(gespeichert ?? { dokumentIds: [...dokumentIds], taskIds: [...taskIds] })}
          >
            <Ikone name="split" />
            Split rückgängig machen
          </button>
        )}
      </div>
    </section>
  );
}
